import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { toast } from 'sonner'
import { confirmDialog } from '@/lib/confirmDialog'
import SaveSearchModal from './SaveSearchModal'
import { canCreateSavedSearch, getSavedSearchLimit, getAllowedAlertFrequencies } from '@/lib/postingLimits'

/**
 * SavedSearches Component
 * 
 * Displays and manages user's saved scrimmage searches with alert preferences
 * 
 * Features:
 * - List all saved searches
 * - Show filters summary
 * - Enable/disable alerts per search
 * - Edit search criteria
 * - Delete searches
 * - Alert frequency management
 */

export default function SavedSearches({ unsubscribeSearchId }) {
  const [searches, setSearches] = useState([])
  const [loading, setLoading] = useState(true)
  const [teamId, setTeamId] = useState(null)
  const [seasonTier, setSeasonTier] = useState('free')
  const [newMatchCounts, setNewMatchCounts] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [editingSearch, setEditingSearch] = useState(null)
  const [showUnsubscribeDialog, setShowUnsubscribeDialog] = useState(false)
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null)

  useEffect(() => {
    loadSearches()
  }, [])

  // Handle unsubscribe action from URL parameters
  useEffect(() => {
    if (unsubscribeSearchId && searches.length > 0) {
      const searchToUnsubscribe = searches.find(search => search.id === unsubscribeSearchId)
      if (searchToUnsubscribe) {
        setUnsubscribeTarget(searchToUnsubscribe)
        setShowUnsubscribeDialog(true)
      }
    }
  }, [unsubscribeSearchId, searches])

  const loadSearches = async () => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to view saved searches')
        return
      }

      // Get user's team
      const { data: team } = await supabase
        .from('teams')
        .select('id, name, season_tier')
        .eq('user_id', session.user.id)
        .single()

      if (!team) {
        toast.error('No team found for your account')
        return
      }

      setTeamId(team.id)
      setSeasonTier(team.season_tier || 'free')

      // Load saved searches
      const { data, error } = await supabase
        .from('scrimmage_searches')
        .select('*')
        .eq('team_id', team.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      const loadedSearches = data || []
      setSearches(loadedSearches)

      // Compute "new matches" counts for in-app badges (best-effort, non-blocking)
      try {
        const userAccessLevel = team.season_tier === 'paid' ? 'pro' : 'free'
        const counts = await Promise.all(
          loadedSearches.map(async (search) => {
            const baseline = new Date(search.last_alerted_at || search.created_at || 0)

            const { data: scrimmages, error: rpcError } = await supabase
              .rpc('search_scrimmages_with_calendar', {
                search_lat: search.filter_location_lat,
                search_lng: search.filter_location_lng,
                radius_miles: search.filter_radius_miles,
                filter_level: search.filter_level,
                filter_level_detail: search.filter_level_detail,
                filter_date_start: search.filter_date_start,
                filter_date_end: search.filter_date_end,
                filter_posting_type: search.filter_posting_type,
                filter_referee_type: search.filter_referee_type,
                filter_home_and_home: search.filter_home_and_home,
                calendar_team_id: search.require_calendar_match ? team.id : null,
                conflict_mode: search.conflict_mode || 'any',
                user_access_level: userAccessLevel
              })

            if (rpcError || !scrimmages) {
              return [search.id, 0]
            }

            const newCount = scrimmages.filter((result) => {
              const createdAt = result?.posting?.created_at ? new Date(result.posting.created_at) : null
              return createdAt && createdAt > baseline
            }).length

            return [search.id, newCount]
          })
        )

        setNewMatchCounts(Object.fromEntries(counts))
      } catch {
        setNewMatchCounts({})
      }
    } catch (error) {
      console.error('[SavedSearches] Error loading searches:', error)
      toast.error('Failed to load saved searches')
    } finally {
      setLoading(false)
    }
  }

  const toggleAlerts = async (search) => {
    try {
      const { error } = await supabase
        .from('scrimmage_searches')
        .update({ alert_enabled: !search.alert_enabled })
        .eq('id', search.id)

      if (error) throw error

      setSearches(searches.map(s => 
        s.id === search.id ? { ...s, alert_enabled: !s.alert_enabled } : s
      ))

      toast.success(
        search.alert_enabled 
          ? 'Alerts disabled for this search' 
          : 'Alerts enabled for this search'
      )
    } catch (error) {
      console.error('[SavedSearches] Error toggling alerts:', error)
      toast.error('Failed to update alert settings')
    }
  }

  const handleDelete = async (search) => {
    const confirmed = await confirmDialog(
      `Delete "${search.search_name}"?`,
      {
        description: 'This will also delete all alert history for this search.',
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    )

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('scrimmage_searches')
        .delete()
        .eq('id', search.id)

      if (error) throw error

      setSearches(searches.filter(s => s.id !== search.id))
      toast.success('Search deleted')
    } catch (error) {
      console.error('[SavedSearches] Error deleting search:', error)
      toast.error('Failed to delete search')
    }
  }

  const handleEdit = (search) => {
    setEditingSearch(search)
    setShowModal(true)
  }

  const handleNewSearch = () => {
    if (!canCreateSavedSearch(searches.length, seasonTier)) {
      const limit = getSavedSearchLimit(seasonTier)
      toast.error(`Free tier limit reached (${limit} saved searches). Upgrade to Pro for unlimited.`)
      return
    }
    setEditingSearch(null)
    setShowModal(true)
  }

  const handleSave = () => {
    setShowModal(false)
    setEditingSearch(null)
    loadSearches()
  }

  const handleUnsubscribe = async (search) => {
    try {
      const { error } = await supabase
        .from('scrimmage_searches')
        .update({ alert_enabled: false })
        .eq('id', search.id)

      if (error) throw error

      toast.success(`Unsubscribed from "${search.search_name}"`)
      loadSearches()
      setShowUnsubscribeDialog(false)
      setUnsubscribeTarget(null)
    } catch (err) {
      console.error('Error unsubscribing:', err)
      toast.error('Failed to unsubscribe')
    }
  }

  const getFiltersSummary = (search) => {
    const parts = []
    
    if (search.filter_level) {
      const levelText = search.filter_level_detail 
        ? `${search.filter_level} ${search.filter_level_detail}`
        : search.filter_level
      parts.push(levelText)
    }
    
    if (search.filter_radius_miles) {
      parts.push(`${search.filter_radius_miles}mi radius`)
    }
    
    if (search.filter_date_start && search.filter_date_end) {
      const start = new Date(search.filter_date_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      const end = new Date(search.filter_date_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      parts.push(`${start} - ${end}`)
    }
    
    if (search.filter_posting_type) {
      parts.push(search.filter_posting_type === 'available' ? 'Home ice' : 'Away games')
    }
    
    if (search.conflict_mode && search.require_calendar_match) {
      const modeText = search.conflict_mode === 'strict' ? 'Free days only' : 
                       search.conflict_mode === 'flexible' ? 'No time overlap' : 'Show all'
      parts.push(`📅 ${modeText}`)
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'No filters'
  }

  const getFrequencyBadge = (frequency) => {
    const variants = {
      immediate: { text: 'Immediate', variant: 'success' },
      daily: { text: 'Daily', variant: 'info' },
      weekly: { text: 'Weekly', variant: 'default' }
    }
    const allowed = getAllowedAlertFrequencies(seasonTier)
    const safeFrequency = allowed.includes(frequency)
      ? frequency
      : (allowed[0] || 'weekly')
    return variants[safeFrequency] || variants.weekly
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Loading saved searches...</p>
        </div>
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-brand-charcoal">
              Saved Searches
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage your saved scrimmage searches and email alerts
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleNewSearch}
          >
            + New Search
          </Button>
        </div>

        {/* Searches List */}
        {searches.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                You haven't saved any searches yet
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Save a search to get email alerts when new scrimmages match your criteria
              </p>
              <Button
                variant="primary"
                onClick={handleNewSearch}
              >
                Create Your First Search
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4">
            {searches.map((search) => (
              <Card key={search.id} className="hover:shadow-card-hover transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Search Name & Status */}
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg text-brand-charcoal">
                        {search.search_name}
                      </h3>
                      {search.alert_enabled && (
                        <Badge variant={getFrequencyBadge(search.alert_frequency).variant}>
                          {getFrequencyBadge(search.alert_frequency).text}
                        </Badge>
                      )}
                      {!search.alert_enabled && (
                        <Badge variant="default">Alerts Off</Badge>
                      )}
                      {newMatchCounts?.[search.id] > 0 && (
                        <Badge variant="amber">
                          +{newMatchCounts[search.id]} new
                        </Badge>
                      )}
                    </div>

                    {/* Filters Summary */}
                    <p className="text-sm text-gray-600 mb-3">
                      {getFiltersSummary(search)}
                    </p>

                    {/* Last Alerted */}
                    {search.last_alerted_at && (
                      <p className="text-xs text-gray-400">
                        Last alert: {new Date(search.last_alerted_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant={search.alert_enabled ? 'outline' : 'primary'}
                      size="sm"
                      onClick={() => toggleAlerts(search)}
                    >
                      {search.alert_enabled ? '🔕 Disable Alerts' : '🔔 Enable Alerts'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(search)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(search)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Save Search Modal */}
      {showModal && (
        <SaveSearchModal
          search={editingSearch}
          teamId={teamId}
          onSave={handleSave}
          onCancel={() => {
            setShowModal(false)
            setEditingSearch(null)
          }}
        />
      )}

      {/* Unsubscribe Dialog */}
      {showUnsubscribeDialog && unsubscribeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h1 className="font-heading text-2xl font-bold mb-2">
                Unsubscribe from "{unsubscribeTarget.search_name}"?
              </h1>
              <p className="text-brand-slate">
                You will no longer receive email alerts for this saved search.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                variant="primary"
                onClick={() => handleUnsubscribe(unsubscribeTarget)}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Unsubscribe
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUnsubscribeDialog(false)
                  setUnsubscribeTarget(null)
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-brand-slate text-center">
                You can re-enable alerts for this search anytime from your settings.
              </p>
            </div>
          </Card>
        </div>
      )}
    </>
  )
}

